/*
 * File: AsanaProjectTableRow.tsx
 * Project: a-a
 * File Created: Tuesday, 29th April 2025 3:13:47 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 13th June 2025 7:08:48 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useEffect, useRef, useState } from 'react';
import zeplinColor from '../../../../theme/Color';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import { DndContext, DragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { AsanaService } from '../../../../service/asana.service';
import { LightTooltip } from '../../../shared/CustomTooltip/CustomTooltip';
import AsanaViewTask from '../AsanaViewTask/AsanaViewTask';
import CustomTextField from '../../../shared/CustomTextField/CustomTextField';
import CustomPopOver from '../../../shared/CustomPopOver/CustomPopOver';
import { useInput } from '../../../../customHook/useInput';
import CustomModal from '../../../shared/CustomModal/CustomModal';
import CustomCard from '../../../shared/CustomCard/CustomCard';

type Response = {
  projectId: string;
  projectName: string;
  updatedDate: string;
  projectLink: string;
  tasksGrouped: { sectionId: string; sectionName: string; tasks: any[] }[];
};

const AsanaProjectTableRow = ({
  data,
  loading,
  setModalOpen,
  assignee,
  getTask,
  setDrawerState,
  drawerState,
  filter,
}: {
  data: Response;
  loading: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  assignee: string[];
  getTask: (clear?: boolean, deleteTask?: boolean) => Promise<void>;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  filter: boolean;
}) => {
  const selectedAsana = sessionStorage.getItem('selected-task-asana');
  const statusTask = sessionStorage.getItem('status-asana-task');
  const [taskGrouped, setTaskGrouped] = useState<Response>(data);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedCardHeight, setDraggedCardHeight] = useState<number>();
  const [aboveCard, setAboveCard] = useState<any>();
  const [belowCard, setBelowCard] = useState<any>();
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(() =>
    data.tasksGrouped.reduce((acc, section) => {
      acc[section.sectionId] = true; // Initialize all sections as open
      return acc;
    }, {} as { [key: string]: boolean }),
  );
  const [confirmModal, setConfirmModal] = useState(false);
  const [loadingMarkCompleted, setLoadingMarkCompleted] = useState(false);
  const moveTask = async (section: string, task: string) => {
    try {
      const res = await AsanaService.moveTask(section, { taskId: task });
      console.log(res);
    } catch (err: any) {
      console.log(err);
    }
  };
  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId], // Toggle the open state for the section
    }));
  };

  const handleDragStart = (event: any) => {
    setActiveId(event.active?.id);
  };

  const handleDragOver = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    const sourceSection = taskGrouped.tasksGrouped.find((section) =>
      section.tasks.some((task) => task.id === active?.id),
    );
    const targetSection = taskGrouped.tasksGrouped.find(
      (section) => section.sectionId === over?.id,
    );

    if (sourceSection && targetSection) {
      // const draggedCardIndex = sourceSection.tasks.findIndex((task) => task.id === active?.id);
      const targetIndex = targetSection.tasks.findIndex((task) => task.id === over?.id);

      const aboveCard = targetIndex > 0 ? targetSection.tasks[targetIndex - 1] : null;
      const belowCards = targetSection.tasks.slice(targetIndex + 1);

      setAboveCard(aboveCard);
      setBelowCard(belowCards);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveId(null);
    setAboveCard(undefined);
    setBelowCard(undefined);

    if (!over) return;

    const sourceSection = taskGrouped.tasksGrouped.find((section) =>
      section.tasks.some((task) => task.id === active?.id),
    );
    const targetSection = taskGrouped.tasksGrouped.find(
      (section) => section.sectionId === over?.id,
    );

    if (!targetSection || targetSection === sourceSection) {
      // If no target column is found or the target is the same as the source, do nothing
      console.log('No valid target column found. Keeping the card in the source column.');
      return;
    }

    if (sourceSection && targetSection) {
      const draggedCardIndex = sourceSection.tasks.findIndex((task) => task.id === active?.id);
      const draggedCard = sourceSection.tasks[draggedCardIndex];

      if (draggedCard) {
        moveTask(over?.id, active?.id);
        setTaskGrouped((prev) => ({
          ...prev,
          tasksGrouped: prev.tasksGrouped.map((section) => {
            if (section === sourceSection) {
              return {
                ...section,
                tasks: section.tasks.filter((_, index) => index !== draggedCardIndex),
              };
            }
            if (section === targetSection) {
              const updatedTasks = [...section.tasks];
              updatedTasks.splice(draggedCardIndex, 0, draggedCard);
              return {
                ...section,
                tasks: updatedTasks,
              };
            }
            return section;
          }),
        }));
      }
    }
  };

  useEffect(() => {
    setTaskGrouped(data);
  }, [data]);
  const handleCloseConfirm = () => {
    setConfirmModal(false);
    sessionStorage.removeItem('selected-task-asana');
  };
  const handleConfirm = async () => {
    try {
      setLoadingMarkCompleted(true);
      const res = await AsanaService.editAsanaTask(selectedAsana || '', {
        completed: statusTask === 'not-completed' ? true : false,
      });
      getTask();
      handleCloseConfirm();
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingMarkCompleted(false);
    }
  };
  return (
    <DndContext onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      {filter && taskGrouped?.tasksGrouped?.filter((val) => val?.tasks?.length)?.length <= 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '32px',
            flex: 1,
            marginTop: '9rem',
            // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
            // height: '100vh',
            // paddingBottom: '30%',
            // overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '24px',
              // flex: 1,
              // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
            }}
          >
            {' '}
            <img src={'/assets/no-tasks.svg'} width={48} height={48} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                // flex: 1,
                // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
              }}
            >
              <Typography variant='heading3' color={zeplinColor.BasicBlack} sx={{}}>
                No Results Found
              </Typography>
              <Typography
                variant='body3'
                color={zeplinColor.Neutral8}
                sx={{ width: '317px', textAlign: 'center' }}
              >
                Please try adjusting your filters to see relevant results.
              </Typography>
            </Box>
          </Box>
          <CustomButton
            disabled={false}
            onClick={() => {
              getTask(true);
            }}
            variant='outlined'
            color='secondary'
            style={{
              width: '150px',
              borderRadius: '14px',
              height: '48px',
              backgroundColor: zeplinColor.Brand1,
              gap: '16px',
              padding: '14px 16px',
            }}
            textStyle={{ color: zeplinColor.Neutral1 }}
            text={'Clear Filters'}
          />
        </Box>
      ) : (
        taskGrouped?.tasksGrouped?.map((section) => (
          <DroppableSection
            key={section.sectionId}
            val={section}
            length={section.tasks.length}
            open={openSections[section.sectionId]}
            setOpen={() => toggleSection(section.sectionId)}
            setModalOpen={setModalOpen}
          >
            {section.tasks.map((task) => (
              <DraggableTask
                key={task.id}
                task={task}
                loading={loading}
                activeId={activeId}
                draggedCardHeight={draggedCardHeight}
                taskGrouped={taskGrouped}
                aboveCardId={aboveCard?.id}
                belowCardId={belowCard}
                open={openSections[section.sectionId]}
                assignee={assignee}
                getTask={getTask}
                setConfirmModal={setConfirmModal}
                setDrawerState={setDrawerState}
                drawerState={drawerState}
              />
            ))}
          </DroppableSection>
        ))
      )}
      <DragOverlay>
        {activeId ? (
          <TableRow
            sx={{
              backgroundColor: zeplinColor.Neutral1,
              cursor: 'grab',
              '&:last-child td, &:last-child th': {
                borderBottom: `2px solid ${zeplinColor.BasicBlack}`,
                borderTop: `2px solid ${zeplinColor.BasicBlack}`,
              },
              td: { borderBottom: `2px solid ${zeplinColor.BasicBlack}` },
              // width: '350px',
            }}
          >
            <TableCell
              sx={{
                minWidth: 550,
                maxWidth: 550,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant='body2'>
                {
                  taskGrouped.tasksGrouped
                    .flatMap((section) => section.tasks)
                    .find((task) => task.id === activeId)?.title
                }
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                minWidth: 250,
                maxWidth: 250,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Box
                  sx={{
                    borderRadius: '100px',
                    padding: '10px',
                    width: '26px',
                    height: '26px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: taskGrouped.tasksGrouped
                      .flatMap((section) => section.tasks)
                      .find((task) => task.id === activeId)?.assigneePhoto
                      ? zeplinColor.Brand1
                      : zeplinColor.Neutral8,
                  }}
                >
                  {taskGrouped.tasksGrouped
                    .flatMap((section) => section.tasks)
                    .find((task) => task.id === activeId)?.assigneePhoto ? (
                    <img
                      src={
                        taskGrouped.tasksGrouped
                          .flatMap((section) => section.tasks)
                          .find((task) => task.id === activeId)?.assigneePhoto
                      }
                      width={'26px'}
                      height={'26px'}
                      style={{ borderRadius: '100px', objectFit: 'cover' }}
                      alt='asana-task-assignee-photo'
                    />
                  ) : (
                    <Typography variant='label1' color={zeplinColor.Neutral1}>
                      {taskGrouped.tasksGrouped
                        .flatMap((section) => section.tasks)
                        .find((task) => task.id === activeId)
                        ?.assignee?.split(' ')
                        .map((value: any, index: number) =>
                          index === 0 ||
                          index ===
                            taskGrouped.tasksGrouped
                              .flatMap((section) => section.tasks)
                              .find((task) => task.id === activeId)
                              ?.assignee?.split(' ').length -
                              1
                            ? value[0]
                            : '',
                        )}
                    </Typography>
                  )}
                </Box>
                <Typography variant='body2'>
                  {
                    taskGrouped.tasksGrouped
                      .flatMap((section) => section.tasks)
                      .find((task) => task.id === activeId)?.assignee
                  }
                </Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{
                minWidth: 250,
                maxWidth: 250,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant='body2'>
                {taskGrouped.tasksGrouped
                  .flatMap((section) => section.tasks)
                  .find((task) => task.id === activeId)?.dueDate
                  ? new Date(
                      taskGrouped.tasksGrouped
                        .flatMap((section) => section.tasks)
                        .find((task) => task.id === activeId)?.dueDate,
                    ).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                  : ''}
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                minWidth: 250,
                maxWidth: 250,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {taskGrouped.tasksGrouped
                .flatMap((section) => section.tasks)
                .find((task) => task.id === activeId)?.priority && (
                <Chip
                  label={
                    <Typography variant='label1'>
                      {
                        taskGrouped.tasksGrouped
                          .flatMap((section) => section.tasks)
                          .find((task) => task.id === activeId)?.priority
                      }
                    </Typography>
                  }
                  sx={{
                    bgcolor: taskGrouped.tasksGrouped
                      .flatMap((section) => section.tasks)
                      .find((task) => task.id === activeId)?.priorityColor,
                    color: zeplinColor.Neutral1,
                    '&.MuiChip-root': {
                      height: '22px',
                      padding: '4px 8px',
                      borderRadius: '50px',
                    },
                    '& .MuiChip-label': {
                      padding: 0,
                    },
                  }}
                />
              )}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 250,
                maxWidth: 250,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {taskGrouped.tasksGrouped
                .flatMap((section) => section.tasks)
                .find((task) => task.id === activeId)?.status && (
                <Chip
                  label={
                    <Typography variant='label1'>
                      {
                        taskGrouped.tasksGrouped
                          .flatMap((section) => section.tasks)
                          .find((task) => task.id === activeId)?.status
                      }
                    </Typography>
                  }
                  sx={{
                    bgcolor: taskGrouped.tasksGrouped
                      .flatMap((section) => section.tasks)
                      .find((task) => task.id === activeId)?.statusColor,
                    color: zeplinColor.Neutral1,
                    '&.MuiChip-root': {
                      height: '22px',
                      padding: '4px 8px',
                      borderRadius: '50px',
                    },
                    '& .MuiChip-label': {
                      padding: 0,
                    },
                  }}
                />
              )}
            </TableCell>
          </TableRow>
        ) : null}
      </DragOverlay>
      <CustomModal handleClose={handleCloseConfirm} open={confirmModal}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,

              // width: '312px',
              // height: '201px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Mark as {statusTask === 'not-completed' ? 'Complete' : 'Incomplete'}
                </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  Are you sure you want to mark this task as <br />
                  {statusTask === 'not-completed' ? 'complete' : 'incomplete'}? You can always undo
                  it later.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',

                  gap: '16px',
                }}
              >
                <Box
                  onClick={handleCloseConfirm}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={loadingMarkCompleted ? () => {} : handleConfirm}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: loadingMarkCompleted
                        ? zeplinColor.Neutral8
                        : zeplinColor.Brand1,
                      cursor: 'pointer',
                      // border: `1px solid ${zeplinColor.BasicBlack}`,
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      Mark {statusTask === 'not-completed' ? 'Complete' : 'Incomplete'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
          {/* {loading ? <CustomLoader /> : ''} */}
        </Box>
      </CustomModal>
    </DndContext>
  );
};

export default AsanaProjectTableRow;

const DroppableSection = ({
  val,
  children,
  length,
  open,
  setOpen,
  setModalOpen,
}: {
  val: {
    sectionId: string;
    sectionName: string;
    tasks: any[];
  };
  children: React.ReactNode;
  length: number;
  open: boolean;
  setOpen: () => void;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { setNodeRef } = useDroppable({ id: val.sectionId });

  return (
    <TableBody
      ref={setNodeRef}
      // sx={{ borderCollapse: 'seperate', borderSpacing: '0px 20px', position: 'relative' }}
    >
      {/* Section Row */}
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' },
          td: { borderBottom: '2px solid #ddd', paddingLeft: '0px', height: '72px' },
        }}
      >
        <TableCell colSpan={6} style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label='expand row' size='small' onClick={setOpen}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <Typography variant='heading3'>
            {val?.sectionName} ({val?.tasks?.length})
          </Typography>
        </TableCell>
        <TableCell colSpan={1}></TableCell>
        <TableCell colSpan={1}></TableCell>
        <TableCell colSpan={1}></TableCell>
        <TableCell colSpan={1}></TableCell>
      </TableRow>

      {/* Task Rows */}
      {open && children}
      {open && (
        <Box ref={setNodeRef}>
          <CustomButton
            disabled={false}
            variant='outlined'
            color='customSecondary'
            style={{
              color: zeplinColor.BasicBlack,
              // width: '50%',
              margin: '10px',
              padding: '14px 24px',
              borderRadius: '14px',
              height: '48px',
              borderWidth: 1,
              borderColor: zeplinColor.Neutral1,
              display: 'flex',
              gap: '8px',
              '&:hover': { backgroundColor: zeplinColor.Neutral3 },
            }}
            text={'Add Task'}
            icon={<img src={'/assets/add.svg'} />}
            onClick={() => {
              setModalOpen(true);
            }}
            // tabindex={5}
          />
        </Box>
      )}
    </TableBody>
  );
};
const DraggableTask = ({
  task,
  loading,
  assignee,
  getTask,
  setConfirmModal,
  setDrawerState,
  drawerState,
}: {
  task: any;
  loading: boolean;
  activeId: string | null;
  draggedCardHeight: number | undefined;
  taskGrouped: Response | null;
  aboveCardId: string | undefined;
  belowCardId: any;
  open: boolean;
  assignee: any[];
  getTask: (clear?: boolean, deleteTask?: boolean) => Promise<void>;
  setConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
}) => {
  const currentUser = sessionStorage.getItem('current-user');
  const theme = useTheme();
  const {
    handleChange: handleSearchChange,
    handleBlur: handleSearchBlur,
    // clearInput: clearComment,
    value: search,
    invalid: invalidSearch,
    didChange: didSearchChange,
  } = useInput('', () => true);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);
  const pointer = useRef<NodeJS.Timeout | null>(null);
  const [timeLimit, setTimeLimit] = useState(false);
  const [taskAnchorEl, setTaskAnchorEl] = useState<HTMLElement | null>(null);
  const openTask = Boolean(taskAnchorEl);
  const idTask = openTask ? 'simple-popover' : undefined;
  const [activeId, setActiveId] = useState<string | null>(null);

  const handleMouseUp = () => {
    // Clear the timeout if the user releases before the delay

    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout?.current);

      longPressTimeout.current = null;

      !timeLimit && !taskAnchorEl && setDrawerState(true);
      !timeLimit && !taskAnchorEl && setActiveId(task.id);
    }

    pointer?.current && clearTimeout(pointer?.current);
    setTimeLimit(false);
  };
  const handleAssign = async (id: string) => {
    try {
      const res = await AsanaService.editAsanaTask(task?.id, {
        assignee: id,
      });
      getTask && getTask();
      setTaskAnchorEl(null);
    } catch (err: any) {
      console.log(err);
    }
  };
  useEffect(() => {
    !drawerState && setActiveId('');
  }, [drawerState]);
  return (
    <>
      {isDragging ? (
        <TableRow
          ref={setNodeRef}
          // {...listeners}
          // {...attributes}
          // onMouseDown={handleMouseDown}
          // onTouchStart={handleMouseDown}
          // onMouseUp={handleMouseUp}
          // onTouchEnd={handleMouseUp}
          sx={{
            height: '50px',
          }}
        ></TableRow>
      ) : (
        <TableRow
          ref={setNodeRef}
          // {...listeners}
          onPointerDown={(e) => {
            setTimeLimit(false);
            const target = e.target as HTMLElement;
            if (target.tagName === 'IMG') {
              const altText = (target as HTMLImageElement).alt;
              console.log('Image Alt:', altText);

              // Perform any specific action based on the alt attribute
              if (altText === 'no-assignee-photo') {
                console.log('Specific image clicked!');
                // setAssigneeTask(task?.id);
                setTaskAnchorEl(target);
              } else if (altText === 'not-completed') {
                setConfirmModal(true);
                sessionStorage.setItem('selected-task-asana', task.id);
                sessionStorage.setItem('status-asana-task', 'not-completed');
              } else if (altText === 'completed') {
                setConfirmModal(true);
                sessionStorage.setItem('selected-task-asana', task.id);
                sessionStorage.setItem('status-asana-task', 'completed');
              }
              return;
            }

            longPressTimeout.current = setTimeout(() => {
              setTimeLimit((prev) => !prev);
            }, 1000); // Long press delay (500ms)
            pointer.current = setTimeout(() => !taskAnchorEl && listeners?.onPointerDown(e), 2000);
          }}
          {...attributes}
          // onMouseDown={handleMouseDown}
          // onTouchStart={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleMouseUp}
          sx={{
            backgroundColor: isDragging ? zeplinColor.Neutral8 : zeplinColor.Neutral1,
            cursor: 'grab',
            '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' },
            td: { borderBottom: `2px solid ${'#ddd'}` },
            ...(!loading && {
              ':hover': {
                '&:last-child td, &:last-child th': {
                  borderBottom: `2px solid ${zeplinColor.BasicBlack}`,
                  borderTop: `2px solid ${zeplinColor.BasicBlack}`,
                },
                td: {
                  borderBottom: `2px solid ${zeplinColor.BasicBlack}`,
                  borderTop: `2px solid ${zeplinColor.BasicBlack}`,
                },
              },
            }),
          }}
        >
          <TableCell
            sx={{
              minWidth: 150,
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {task?.completed ? (
                  <LightTooltip
                    title={
                      <Typography variant='body3' color={zeplinColor.Neutral1}>
                        Mark as complete
                      </Typography>
                    }
                    placement='bottom'
                    style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                  >
                    <img
                      src={'/assets/completed.png'}
                      width={'24px'}
                      height={'24px'}
                      style={{ borderRadius: '100px', objectFit: 'cover' }}
                      alt='completed'
                    />
                  </LightTooltip>
                ) : (
                  <LightTooltip
                    title={
                      <Typography variant='body3' color={zeplinColor.Neutral1}>
                        Mark as incomplete
                      </Typography>
                    }
                    placement='bottom'
                    style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                  >
                    <img
                      src={'/assets/mark-as-read.png'}
                      width={'24px'}
                      height={'24px'}
                      style={{ borderRadius: '100px', objectFit: 'cover' }}
                      alt='not-completed'
                    />
                  </LightTooltip>
                )}
                {task?.title?.length < 60 ? (
                  <Typography variant='body2'>{task?.title}</Typography>
                ) : (
                  <LightTooltip
                    title={
                      <Typography variant='body2' color={zeplinColor.Neutral1}>
                        {task?.title}
                      </Typography>
                    }
                    placement='bottom-end'
                    style={{
                      backgroundColor: zeplinColor.BasicBlack,
                      borderRadius: '8px',
                      padding: '12px',
                    }}
                    // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
                  >
                    <Typography variant='body2'>
                      {task?.title?.slice(0, 59)}
                      {task?.title?.length > 60 ? '...' : ''}
                    </Typography>
                  </LightTooltip>
                )}
                {/* <Typography variant='body2'>...</Typography> */}
              </Box>
            )}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 50,
              maxWidth: 50,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  borderRadius: '100px',
                  padding: '10px',
                  width: '26px',
                  height: '26px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: task?.assigneePhoto ? zeplinColor.Brand1 : zeplinColor.Neutral8,
                }}
              >
                {loading ? (
                  <Skeleton variant='rounded' width={26} height={26} />
                ) : !task?.assignee ? (
                  <img
                    src={'/assets/no-assignee.png'}
                    width={'26px'}
                    height={'26px'}
                    style={{ borderRadius: '100px', objectFit: 'cover' }}
                    alt='no-assignee-photo'
                  />
                ) : task?.assigneePhoto ? (
                  <img
                    src={task?.assigneePhoto}
                    width={'26px'}
                    height={'26px'}
                    style={{ borderRadius: '100px', objectFit: 'cover' }}
                    alt='asana-task-assignee-photo'
                  />
                ) : (
                  <Typography variant='label1' color={zeplinColor.Neutral1}>
                    {task?.assignee
                      ?.split(' ')
                      .map((value: any, index: number) =>
                        index === 0 || index === task?.assignee?.split(' ').length - 1
                          ? value[0]
                          : '',
                      )}
                  </Typography>
                )}
              </Box>
              {loading ? (
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    width: '100%',
                    // Optionally adjust other styles here
                  }}
                />
              ) : !task?.assignee?.length ? (
                ''
              ) : task?.assignee?.length < 20 ? (
                <Typography variant='body2'>{task?.assignee?.slice(0, 20)}</Typography>
              ) : (
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      {task?.assignee}
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                >
                  <Typography variant='body2'>{task?.assignee?.slice(0, 19)}...</Typography>
                </LightTooltip>
              )}
            </Box>
          </TableCell>
          <TableCell
            sx={{
              minWidth: 50,
              maxWidth: 50,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
                // justifyContent: 'flex-end',
              }}
            >
              {loading ? (
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    width: '100%',
                    // Optionally adjust other styles here
                  }}
                />
              ) : (
                <Typography
                  variant='body2'
                  color={task?.overdue ? zeplinColor.Error : zeplinColor.BasicBlack}
                >
                  {task?.dueDate
                    ? new Date(task?.dueDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'NA'}
                </Typography>
              )}
              {task?.overdue && (
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      Overdue
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                >
                  <img src='/assets/overdue-icon.svg' width={18} height={18} style={{}} />
                </LightTooltip>
              )}
            </Box>
          </TableCell>
          <TableCell
            sx={{
              minWidth: 50,
              maxWidth: 50,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  // Optionally adjust other styles here
                }}
              />
            ) : task?.priority ? (
              <Chip
                label={<Typography variant='label1'>{task?.priority}</Typography>}
                sx={{
                  bgcolor: task?.priorityColor,
                  color: zeplinColor.Neutral1,
                  '&.MuiChip-root': {
                    height: '22px',
                    padding: '4px 8px',
                    borderRadius: '50px',
                  },
                  '& .MuiChip-label': {
                    padding: 0,
                  },
                }}
              />
            ) : (
              <Typography variant='body2'>NA</Typography>
            )}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 50,
              maxWidth: 50,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  // Optionally adjust other styles here
                }}
              />
            ) : task?.status ? (
              <Chip
                label={<Typography variant='label1'>{task?.status}</Typography>}
                sx={{
                  bgcolor: task?.statusColor,
                  color: zeplinColor.Neutral1,
                  '&.MuiChip-root': {
                    height: '22px',
                    padding: '4px 8px',
                    borderRadius: '50px',
                  },
                  '& .MuiChip-label': {
                    padding: 0,
                  },
                }}
              />
            ) : (
              <Typography variant='body2'>NA</Typography>
            )}
          </TableCell>
          <CustomPopOver
            setAnchorEl={setTaskAnchorEl}
            anchorEl={taskAnchorEl}
            id={idTask}
            sx={{
              borderRadius: '14px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              marginTop: '8px',
              // display: 'flex',

              // marginRight: '2rem',
              // marginLeft: '-4rem',
            }}
            horizontal='right'
            vertical='top'
          >
            <Box
              sx={{
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                width: '399px',
                height: '270px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  width: '100%',
                }}
              >
                <Typography variant='heading2'>Assignee</Typography>
                <img
                  src={'/assets/close-icon.svg'}
                  width={24}
                  height={24}
                  alt='close'
                  style={{ cursor: 'pointer' }}
                  onClick={() => setTaskAnchorEl(null)}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  width: '100%',
                }}
              >
                <CustomTextField
                  style={{ width: '100%' }}
                  onBlur={handleSearchBlur}
                  onChange={handleSearchChange}
                  name='Name'
                  text={search}
                  error={invalidSearch && didSearchChange ? 'Please enter valid Comment' : ''}
                  label={'Type name or email'}
                  type='text'
                  // tabindex={1}
                  // multiline
                  // required
                />
                <CustomButton
                  disabled={
                    assignee?.filter((val: any) =>
                      val?.name?.toLocaleLowerCase()?.includes(currentUser?.toLocaleLowerCase()),
                    )?.length === 0
                  }
                  variant='outlined'
                  color='customSecondary'
                  style={{
                    color: zeplinColor.BasicBlack,
                    width: '50%',
                    margin: 'auto',
                    borderRadius: '14px',
                    height: 56,
                    borderWidth: 1,
                    borderColor: zeplinColor.Neutral9,
                    // display: 'flex',
                    // gap: '16px',
                  }}
                  text={'Assign to me'}
                  // icon={<img src={'/assets/microsoft-logo.svg'} />}
                  onClick={() => {
                    const id = assignee?.filter((val: any) =>
                      val?.name?.toLocaleLowerCase()?.includes(currentUser?.toLocaleLowerCase()),
                    )[0]?.id;
                    handleAssign(id);
                  }}
                  // tabindex={5}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '12px',
                  alignSelf: 'stretch',
                }}
              >
                {assignee?.length &&
                  assignee
                    .filter((val: any) =>
                      val?.name?.toLocaleLowerCase()?.includes(search?.trim()?.toLocaleLowerCase()),
                    )
                    .map((val) => (
                      <Box
                        sx={{
                          display: 'flex',
                          // flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          alignSelf: 'stretch',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleAssign(val?.id)}
                      >
                        <Box
                          sx={{
                            borderRadius: '100px',
                            padding: '10px',
                            width: '26px',
                            height: '26px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: task?.assigneePhoto
                              ? zeplinColor.Brand1
                              : zeplinColor.Neutral8,
                          }}
                        >
                          <img
                            src={'/assets/no-assignee.png'}
                            width={'26px'}
                            height={'26px'}
                            style={{ borderRadius: '100px', objectFit: 'cover' }}
                            alt='no-assignee-photo'
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant='body2'>{val?.name}</Typography>
                          <Typography variant='body2'>{val?.name}</Typography>
                        </Box>
                      </Box>
                    ))}
              </Box>
            </Box>
          </CustomPopOver>
        </TableRow>
      )}

      {task?.id === activeId && (
        <AsanaViewTask
          setDrawerState={setDrawerState}
          drawerState={drawerState}
          selectedData={task}
          getTask={getTask}
          // setDeleted={setDeleted}
          // setEditDone={setEdit}
          // setSelected={setSelected}
        />
      )}
    </>
  );
};
